import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { toast } from 'sonner';
import { Upload, Eye, EyeOff, Trash2, Crosshair, Loader2 } from 'lucide-react';
import { parseDxf, dxfToWgs, guessCrs, localMeta } from '../lib/dxf';
import { parseDwg } from '../lib/dwg';
import { setVis, flyTo } from '../lib/vis';
import CadAdopt from './CadAdopt';
import PlanCheck from './PlanCheck';

const COLORS = ['#e11d48', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];
export const colorOf = (layer) => COLORS[[...String(layer)].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// CAD vector reference layer on the map (clickable, below own network)
export function useCadLayer(mapRef, cad, on = true) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !cad || cad.hidden || !on) return undefined;
    if (!map.getPane('cad')) map.createPane('cad').style.zIndex = 420;
    const op = (cad.opacity ?? 80) / 100, off = new Set(cad.off || []), pane = 'cad';
    const g = L.layerGroup().addTo(map);
    cad.ents.filter((e) => !off.has(e.layer)).forEach((e) => {
      const c = colorOf(e.layer), pop = `<b>CAD</b> · Layer <b>${esc(e.layer)}</b>${e.text ? `<br>${esc(e.text)}` : ''}`;
      if (e.type === 'line') g.addLayer(L.polyline(e.pts, { pane, color: c, weight: 2.5, opacity: op, className: 'cad-line' }).bindPopup(pop));
      else if (e.type === 'text') g.addLayer(L.marker(e.p, { pane, icon: L.divIcon({ className: 'cad-txt-wrap', html: `<span class="cad-txt" style="color:${c};opacity:${op}">${esc(e.text)}</span>`, iconSize: [0, 0] }) }).bindPopup(pop));
      else g.addLayer(L.circleMarker(e.p, { pane, radius: 3.5, color: c, weight: 1.5, opacity: op, fillOpacity: op * 0.6, className: 'cad-pt' }).bindPopup(pop));
    });
    return () => g.remove();
  }, [mapRef, cad, on]);
}

export const cadPts = (cad) => (cad?.ents || []).flatMap((e) => e.pts || [e.p]);
export const cadLayers = (cad) => Object.entries((cad?.ents || []).reduce((a, e) => ({ ...a, [e.layer]: (a[e.layer] || 0) + 1 }), {}));

const CRS = ['auto', 'lv95', 'lv03', 'wgs84', 'utm31', 'utm32', 'utm33', 'local'];

// import / settings of the CAD overlay (stored in the project, offline)
export default function CadPanel({ t, project, updateProject, getCenter }) {
  const cad = project.cad;
  const inRef = useRef(null);
  const [crs, setCrs] = useState('auto');
  const [busy, setBusy] = useState(false);
  const set = (p) => updateProject((pr) => ({ ...pr, cad: { ...pr.cad, ...p } }));
  const near = project.lat != null ? [project.lat, project.lon] : project.reports?.[0] ? [project.reports[0].lat, project.reports[0].lon] : null;
  const here = () => { const c = getCenter?.(); return c ? [c.lat, c.lng] : near || [46.06, 11.12]; };
  const build = (raw, c, name, unit = 1, loc0 = null) => {
    const sample = raw[0]?.pts?.[0] || raw[0]?.p;
    const use = c === 'auto' ? guessCrs(sample, near, true) : c;
    const loc = use === 'local' ? loc0 || localMeta(raw, here(), unit) : null;
    const ents = dxfToWgs(raw, use, loc);
    if (!ents.length) throw new Error('crs');
    return { name, crs: use, raw, ents, unit, loc, hidden: false, opacity: cad?.opacity ?? 80, tol: cad?.tol ?? 1, check: cad?.check || false };
  };
  const show = (next) => { setVis({ cad: true }); flyTo(cadPts(next), true); };
  const onFile = async (f) => {
    const dwg = /\.dwg$/i.test(f.name);
    setBusy(dwg);
    try {
      const { ents: raw, unit } = dwg ? await parseDwg(new Uint8Array(await f.arrayBuffer())) : { ents: parseDxf(await f.text()), unit: 1 };
      const next = build(raw, crs, f.name, unit);
      updateProject((pr) => ({ ...pr, cad: next }));
      show(next);
      toast.success(`${t.cadLoaded}: ${next.ents.length} · ${next.crs.toUpperCase()}`);
    } catch (e) { toast.error(e.message === 'crs' ? t.cadCrsErr : e.message === 'dwgOld' ? t.cadDwgOld : e.message === 'dwgNet' ? t.cadDwgNet : e.message === 'dwgKritis' ? t.cadDwgKritis : e.message === 'dwgLic' ? t.cadDwgLic : dwg ? t.cadDwgErr : t.cadErr); }
    setBusy(false);
  };
  const reproject = (c) => { setCrs(c); if (cad) { try { const n = build(cad.raw, c, cad.name, cad.unit, c === 'local' ? cad.loc : null); set({ ...n, off: cad.off || [], hidden: cad.hidden }); show(n); } catch { toast.error(t.cadCrsErr); } } };
  const replace = () => { const n = build(cad.raw, 'local', cad.name, cad.unit, { ...cad.loc, anchor: here() }); set({ ...n, off: cad.off || [], hidden: cad.hidden }); show(n); };
  return (
    <div className="cad-panel" data-testid="cad-panel">
      <div className="row">
        <b>📐 {t.cadTitle}</b>
        <select data-testid="cad-crs-select" value={crs} onChange={(e) => reproject(e.target.value)}>{CRS.map((c) => <option key={c} value={c}>{c === 'auto' ? t.cadAuto : c === 'local' ? t.cadLocal : c.toUpperCase()}</option>)}</select>
        <button className="btn btn-dark btn-sm" data-testid="cad-import-btn" disabled={busy} onClick={() => inRef.current.click()}>{busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} {busy ? t.cadDwgBusy : t.cadImport}</button>
        <input ref={inRef} type="file" accept=".dxf,.dwg" hidden data-testid="cad-file-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
      </div>
      {!cad && <p className="muted small-hint">{t.cadHint}</p>}
      {cad && (
        <div className="row" data-testid="cad-info">
          <small><b data-testid="cad-name">{cad.name}</b> · {cad.ents.length} {t.cadEnts} · <span data-testid="cad-crs">{cad.crs.toUpperCase()}</span></small>
          {cad.crs === 'local' && <button className="btn btn-outline btn-sm" data-testid="cad-replace-btn" title={t.cadReplaceHint} onClick={replace}>{t.cadReplace}</button>}
          <button className="icon-btn" data-testid="cad-zoom-btn" title={t.cadZoom} onClick={() => flyTo(cadPts(cad), true)}><Crosshair size={15} /></button>
          <input type="range" min="10" max="100" value={cad.opacity ?? 80} data-testid="cad-opacity-slider" onChange={(e) => set({ opacity: +e.target.value })} />
          <button className="icon-btn" data-testid="cad-toggle-btn" onClick={() => set({ hidden: !cad.hidden })}>{cad.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
          <button className="icon-btn danger" data-testid="cad-delete-btn" onClick={() => window.confirm(t.cadDelQ) && updateProject((pr) => ({ ...pr, cad: null }))}><Trash2 size={15} /></button>
        </div>
      )}
      {cad && <CadAdopt t={t} project={project} updateProject={updateProject} />}
      {cad && <PlanCheck t={t} project={project} updateProject={updateProject} />}
    </div>
  );
}
